import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Printer, ArrowUpRight, Clock } from "lucide-react";
import { CONTACT, LOGO_URL, NAV_LINKS } from "../../constants/siteData";

export const Footer = () => {
  return (
    <footer
      id="contact"
      className="relative bg-[#061223] pt-24 md:pt-32 pb-10 border-t hairline overflow-hidden"
      data-testid="footer-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Big CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-20 md:mb-28"
        >
          <p className="kicker mb-6">Let&apos;s start working together</p>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-4xl">
            Ready to stock the shelves the world <span className="text-coral">already loves?</span>
          </h2>
          <a
            href={`mailto:${CONTACT.emailInfo}`}
            className="btn-coral mt-10 text-base"
            data-testid="footer-cta-email"
          >
            Contact us now <ArrowUpRight size={18} />
          </a>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b hairline">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Avado" className="h-12" />
              <span className="font-display text-lg text-cream tracking-tight whitespace-nowrap">
                AVADO GLOBAL TRADING
              </span>
            </div>
            <p className="text-white/60 leading-relaxed max-w-sm">
              A leading USA wholesale, import and export company specialising in health &amp;
              beauty aids, food and beverages — actively delivering across Africa, Europe, UK, USA, GCC and Caribbean.
            </p>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-5 grid sm:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <MapPin className="text-coral shrink-0 mt-1" size={20} />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">
                  Office
                </p>
                <p className="text-cream/90 leading-relaxed" data-testid="footer-address">
                  {CONTACT.address}
                </p>
                <p className="text-white/50 text-sm mt-2 flex items-center gap-2">
                  <Clock size={12} /> {CONTACT.hours}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Mail className="text-coral shrink-0 mt-1" size={20} />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">
                  Email
                </p>
                <a
                  href={`mailto:${CONTACT.emailInfo}`}
                  className="text-cream/90 hover:text-coral block"
                  style={{ transitionProperty: "color", transitionDuration: "200ms" }}
                  data-testid="footer-email-info"
                >
                  {CONTACT.emailInfo}
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-white/60 text-sm hover:text-coral block mt-1"
                  style={{ transitionProperty: "color", transitionDuration: "200ms" }}
                  data-testid="footer-email-secondary"
                >
                  {CONTACT.email}
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Phone className="text-coral shrink-0 mt-1" size={20} />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">
                  Office phone
                </p>
                <a
                  href={`tel:${CONTACT.office}`}
                  className="text-cream/90 hover:text-coral block"
                  style={{ transitionProperty: "color", transitionDuration: "200ms" }}
                  data-testid="footer-phone"
                >
                  {CONTACT.office}
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Printer className="text-coral shrink-0 mt-1" size={20} />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">
                  Fax
                </p>
                <p className="text-cream/90" data-testid="footer-fax">
                  {CONTACT.fax}
                </p>
              </div>
            </div>
          </div>

          {/* Nav column */}
          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-5">
              Explore
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-cream/80 hover:text-coral flex items-center gap-2 group"
                    style={{ transitionProperty: "color", transitionDuration: "200ms" }}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                  >
                    <span className="w-4 h-[1px] bg-white/20 group-hover:bg-coral group-hover:w-8"
                      style={{ transitionProperty: "background-color, width", transitionDuration: "200ms" }}
                    />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Avado Global Trading LLC. All rights reserved.
          </p>
          <p className="text-white/40 text-xs tracking-widest uppercase">
            Actively Delivering — USA · Caribbean · Africa · Europe · UK · GCC
          </p>
        </div>
      </div>
    </footer>
  );
};
